import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine,
  AreaChart,
  Area
} from 'recharts';
import { apiService, AnalyticsResponse, Insight, WeeklySpendSummary } from '../services/api';
import { usePrivacy } from '../contexts/PrivacyContext';
import { DateRangePicker, DateRange } from '../components/ui/date-range-picker';
import { InsightsCarousel } from '../components/InsightsCarousel';
import { TrendingUp, TrendingDown, Wallet, PiggyBank, ArrowUpRight, ArrowDownRight, Calendar } from 'lucide-react';

// Modern, Apple-inspired color palette
const CATEGORY_COLORS: Record<string, string> = {
  'Restaurants and meals': '#FF6B6B',
  'Travel and tourism': '#FF8C42',
  'Taxi and transportation': '#FFD93D',
  'Gas': '#C1FF72',
  'Utilities & Phone': '#6BCB77',
  'Entertainment': '#4ECDC4',
  'Groceries': '#45B7D1',
  'Subscriptions': '#96CEB4',
  'Home Expenses': '#88D8B0',
  'Loan and mortgage payments': '#5B8DEE',
  'Fitness and health': '#7C83FD',
  'Shopping': '#A66DD4',
  'Uncategorized': '#B8B5FF',
  'Employment Income': '#4ECDC4',
  'Self-Employment Income': '#45B7D1',
  'Investment Income': '#6BCB77',
  'Government & Retirement Income': '#5B8DEE',
  'Other / One-Time Income': '#7C83FD',
};

const getCategoryColor = (category: string, index: number): string => {
  if (CATEGORY_COLORS[category]) {
    return CATEGORY_COLORS[category];
  }
  const fallbackColors = ['#94A3B8', '#64748B', '#475569', '#334155', '#1E293B'];
  return fallbackColors[index % fallbackColors.length];
};

// Custom Apple-style tooltip
const AppleTooltip = ({ active, payload, label, formatter }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="apple-tooltip" style={{ backgroundColor: 'var(--theme-surface)', color: 'var(--theme-text)' }}>
        <div className="font-semibold text-xs mb-2" style={{ color: 'var(--theme-text-secondary)' }}>{label}</div>
        <div className="space-y-1.5">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-xs truncate max-w-[120px]" style={{ color: 'var(--theme-text-secondary)' }}>{entry.name}</span>
              </div>
              <span className="text-xs font-semibold" style={{ color: 'var(--theme-text)' }}>
                {formatter ? formatter(entry.value) : entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

// Stat card component with Apple-style design
interface StatCardProps {
  title: string;
  value: string;
  change?: number;
  changeLabel?: string;
  icon: React.ReactNode;
  color: string;
  delay?: number;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, changeLabel, icon, color, delay = 0 }) => {
  const isPositive = change !== undefined && change >= 0;
  
  return (
    <div 
      className="apple-card group cursor-default"
      style={{ animationDelay: `${delay}ms`, backgroundColor: 'var(--theme-surface)' }}
    >
      <div className="flex items-start justify-between mb-3">
        <div 
          className="p-2.5 rounded-xl transition-transform duration-300 group-hover:scale-110"
          style={{ backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)` }}
        >
          <div style={{ color }}>{icon}</div>
        </div>
        {change !== undefined && (
          <div 
            className="flex items-center gap-0.5 text-xs font-medium"
            style={{ color: isPositive ? 'var(--theme-income)' : 'var(--theme-expense)' }}
          >
            {isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            <span>{Math.abs(change).toFixed(1)}%</span>
          </div>
        )}
      </div>
      <div className="space-y-0.5">
        <p className="text-2xl font-bold tracking-tight" style={{ color: 'var(--theme-text)' }}>{value}</p>
        <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>{title}</p>
        {changeLabel && (
          <p className="text-[10px]" style={{ color: 'var(--theme-text-secondary)', opacity: 0.7 }}>{changeLabel}</p>
        )}
      </div>
    </div>
  );
};

// Category legend pill
interface CategoryPillProps {
  category: string;
  color: string;
  amount: number;
  onClick?: () => void;
}

const CategoryPill: React.FC<CategoryPillProps & { isPrivacyMode?: boolean }> = ({ category, color, amount, onClick, isPrivacyMode }) => {
  const formatCurrency = (value: number) => {
    if (isPrivacyMode) return '$••••';
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
    return `$${value.toFixed(0)}`;
  };

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium 
                 backdrop-blur-sm border
                 hover:shadow-md hover:scale-105 
                 transition-all duration-200 cursor-pointer"
      style={{ 
        backgroundColor: 'color-mix(in srgb, var(--theme-surface) 80%, transparent)',
        borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)'
      }}
    >
      <span 
        className="w-2 h-2 rounded-full flex-shrink-0" 
        style={{ backgroundColor: color }}
      />
      <span className="truncate max-w-[100px]" style={{ color: 'var(--theme-text)' }}>{category}</span>
      <span style={{ color: 'var(--theme-text-secondary)' }}>{formatCurrency(amount)}</span>
    </button>
  );
};

export const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isPrivacyMode } = usePrivacy();
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weeklySummary, setWeeklySummary] = useState<WeeklySpendSummary | null>(null);
  const [weeklySummaryLoading, setWeeklySummaryLoading] = useState(true);
  const [weeklySummaryError, setWeeklySummaryError] = useState<string | null>(null);
  
  // Insights state
  const [insights, setInsights] = useState<Insight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(true);


  // Date range state
  const getDefaultDateRange = (): DateRange => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 3);
    return { from: start, to: end };
  };

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());

  const fetchAnalytics = async (range: DateRange, signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const startDate = range.from.toISOString().split('T')[0];
      const endDate = range.to.toISOString().split('T')[0];
      const response = await apiService.getAnalytics(startDate, endDate, signal);
      if (!signal?.aborted) {
        setAnalytics(response);
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      if (!signal?.aborted) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const fetchInsights = useCallback(async (signal?: AbortSignal) => {
    try {
      setInsightsLoading(true);
      const response = await apiService.getInsights(false, signal);
      if (!signal?.aborted) {
        setInsights(response.insights);
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Failed to load insights:', err);
    } finally {
      if (!signal?.aborted) {
        setInsightsLoading(false);
      }
    }
  }, []);

  const fetchWeeklySummary = useCallback(async (range: DateRange, signal?: AbortSignal) => {
    try {
      setWeeklySummaryLoading(true);
      setWeeklySummaryError(null);
      const startDate = range.from.toISOString().split('T')[0];
      const endDate = range.to.toISOString().split('T')[0];
      const response = await apiService.getWeeklySpendSummary(startDate, endDate, signal);
      if (!signal?.aborted) {
        setWeeklySummary(response.summary);
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      if (!signal?.aborted) {
        setWeeklySummaryError(err instanceof Error ? err.message : 'Failed to load weekly summary');
      }
    } finally {
      if (!signal?.aborted) {
        setWeeklySummaryLoading(false);
      }
    }
  }, []);
  
  const handleInsightVote = useCallback(async (id: string, vote: 1 | -1) => {
    try {
      await apiService.voteInsight(id, vote);
      setInsights(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error('Failed to vote on insight:', err);
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    fetchAnalytics(dateRange, abortController.signal);
    fetchWeeklySummary(dateRange, abortController.signal);
    return () => abortController.abort();
  }, [dateRange, fetchWeeklySummary]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchInsights(abortController.signal);
    return () => abortController.abort();
  }, [fetchInsights]);


  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  // Process data for net income trend (area chart instead of waterfall)
  const netIncomeData = useMemo(() => {
    if (!analytics) return [];
    
    let cumulative = 0;
    return analytics.monthly_spend.map((month) => {
      const income = parseFloat(month.income);
      const expenses = parseFloat(month.expenses);
      const netIncome = income - expenses;
      cumulative += netIncome;
      
      return {
        month: month.month,
        netIncome,
        cumulative,
        income,
        expenses,
      };
    });
  }, [analytics]);

  // Process expense data with totals
  const { expenseChartData, expenseCategories, categoryTotals: expenseCategoryTotals } = useMemo(() => {
    if (!analytics) return { expenseChartData: [], expenseCategories: [], categoryTotals: {} };
    
    const monthMap = new Map<string, Record<string, number>>();
    const totals: Record<string, number> = {};
    
    analytics.monthly_expense_by_category.forEach((item) => {
      if (!monthMap.has(item.month)) {
        monthMap.set(item.month, {});
      }
      const monthData = monthMap.get(item.month)!;
      const amount = parseFloat(item.amount);
      monthData[item.category] = amount;
      totals[item.category] = (totals[item.category] || 0) + amount;
    });
    
    const categories = new Set<string>();
    analytics.monthly_expense_by_category.forEach(item => categories.add(item.category));
    
    return {
      expenseChartData: Array.from(monthMap.entries()).map(([month, categories]) => ({
        month,
        ...categories
      })),
      expenseCategories: Array.from(categories),
      categoryTotals: totals
    };
  }, [analytics]);

  // Process income data with totals
  const { incomeChartData, incomeCategories, categoryTotals: incomeCategoryTotals } = useMemo(() => {
    if (!analytics) return { incomeChartData: [], incomeCategories: [], categoryTotals: {} };
    
    const monthMap = new Map<string, Record<string, number>>();
    const totals: Record<string, number> = {};
    
    analytics.monthly_income_by_category.forEach((item) => {
      if (!monthMap.has(item.month)) {
        monthMap.set(item.month, {});
      }
      const monthData = monthMap.get(item.month)!;
      const amount = parseFloat(item.amount);
      monthData[item.category] = amount;
      totals[item.category] = (totals[item.category] || 0) + amount;
    });
    
    const categories = new Set<string>();
    analytics.monthly_income_by_category.forEach(item => categories.add(item.category));
    
    return {
      incomeChartData: Array.from(monthMap.entries()).map(([month, categories]) => ({
        month,
        ...categories
      })),
      incomeCategories: Array.from(categories),
      categoryTotals: totals
    };
  }, [analytics]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!analytics || !netIncomeData.length) return null;
    
    const totalIncome = netIncomeData.reduce((sum, d) => sum + d.income, 0);
    const totalExpenses = netIncomeData.reduce((sum, d) => sum + d.expenses, 0);
    const netTotal = totalIncome - totalExpenses;
    const avgMonthlyExpense = totalExpenses / netIncomeData.length;
    
    // Calculate change vs previous period (simplified)
    const midpoint = Math.floor(netIncomeData.length / 2);
    const firstHalf = netIncomeData.slice(0, midpoint);
    const secondHalf = netIncomeData.slice(midpoint);
    
    const firstHalfExpense = firstHalf.reduce((sum, d) => sum + d.expenses, 0);
    const secondHalfExpense = secondHalf.reduce((sum, d) => sum + d.expenses, 0);
    const expenseChange = firstHalfExpense > 0 
      ? ((secondHalfExpense - firstHalfExpense) / firstHalfExpense) * 100 
      : 0;
    
    const firstHalfIncome = firstHalf.reduce((sum, d) => sum + d.income, 0);
    const secondHalfIncome = secondHalf.reduce((sum, d) => sum + d.income, 0);
    const incomeChange = firstHalfIncome > 0 
      ? ((secondHalfIncome - firstHalfIncome) / firstHalfIncome) * 100 
      : 0;
    
    return {
      totalIncome,
      totalExpenses,
      netTotal,
      avgMonthlyExpense,
      expenseChange,
      incomeChange
    };
  }, [analytics, netIncomeData]);

  // Navigation handlers
  const handleChartClick = (data: any, type: 'expense' | 'income', category?: string) => {
    if (data && data.month) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const [monthName, year] = data.month.split(' ');
      const monthIndex = monthNames.indexOf(monthName);

      if (monthIndex !== -1) {
        const startDate = new Date(parseInt(year), monthIndex, 1);
        const endDate = new Date(parseInt(year), monthIndex + 1, 0);

        let url = `/transactions?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`;
        if (category) {
          url += `&category=${encodeURIComponent(category)}&type=${type}`;
        }
        navigate(url);
      }
    }
  };

  const handleTableCellClick = (category: string, weekIndex?: number) => {
    let url = `/transactions?category=${encodeURIComponent(category)}&type=expense`;

    if (weekIndex !== undefined && weeklySummary?.weeks[weekIndex]) {
      const week = weeklySummary.weeks[weekIndex];
      const weekStart = new Date(week.start);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // Add 6 days to get end of week

      url += `&start_date=${weekStart.toISOString().split('T')[0]}&end_date=${weekEnd.toISOString().split('T')[0]}`;
    }

    navigate(url);
  };

  const formatCurrency = (value: number) => {
    if (isPrivacyMode) return '$••••';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyShort = (value: number) => {
    if (isPrivacyMode) return '$••••';
    if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    }
    return `$${value.toFixed(0)}`;
  };

  const formatCurrencyZero = (value: string | number) => {
    if (isPrivacyMode) return '$••••';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };


  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-background)' }}>
        <div className="flex flex-col items-center gap-4">
          <div 
            className="w-10 h-10 border-2 rounded-full animate-spin" 
            style={{ 
              borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 30%, transparent)',
              borderTopColor: 'var(--theme-primary)'
            }}
          />
          <p className="text-sm font-medium" style={{ color: 'var(--theme-text-secondary)' }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-background)' }}>
        <div className="text-center">
          <p className="font-medium" style={{ color: 'var(--theme-expense)' }}>{error}</p>
          <button 
            onClick={() => {
              setError(null);
              fetchAnalytics(dateRange);
            }}
            className="mt-4 px-4 py-2 text-sm font-medium text-white rounded-xl transition-all
                       hover:opacity-90 active:scale-95"
            style={{ backgroundColor: 'var(--theme-primary)' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="analytics-page"
      style={{ backgroundColor: 'var(--theme-background)' }}
    >
      {/* Header */}
      <div 
        className="px-4 py-5 md:px-8 md:py-6 border-b"
        style={{ borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)' }}
      >
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="fade-in-up">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: 'var(--theme-text)' }}>Analytics</h1>
            <p className="text-sm mt-1 hidden md:block" style={{ color: 'var(--theme-text-secondary)' }}>Your financial overview at a glance</p>
          </div>
          <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 pb-6 md:px-8 md:pb-8">
        {/* AI Insights */}
        <div className="mt-4 md:mt-6 fade-in-up" style={{ animationDelay: '100ms' }}>
          <InsightsCarousel
            insights={insights}
            onVote={handleInsightVote}
            onShowAll={() => navigate('/analytics')}
            loading={insightsLoading}
          />
        </div>

        {/* Summary Stats */}
        {summaryStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-6">
            <StatCard
              title="Total Income"
              value={formatCurrency(summaryStats.totalIncome)}
              change={summaryStats.incomeChange}
              changeLabel="vs previous period"
              icon={<TrendingUp className="h-5 w-5" />}
              color="var(--theme-income)"
              delay={200}
            />
            <StatCard
              title="Total Expenses"
              value={formatCurrency(summaryStats.totalExpenses)}
              change={summaryStats.expenseChange}
              changeLabel="vs previous period"
              icon={<TrendingDown className="h-5 w-5" />}
              color="var(--theme-expense)"
              delay={250}
            />
            <StatCard
              title="Net Savings"
              value={formatCurrency(summaryStats.netTotal)}
              icon={<PiggyBank className="h-5 w-5" />}
              color={summaryStats.netTotal >= 0 ? 'var(--theme-income)' : 'var(--theme-expense)'}
              delay={300}
            />
            <StatCard
              title="Monthly Avg"
              value={formatCurrency(summaryStats.avgMonthlyExpense)}
              icon={<Wallet className="h-5 w-5" />}
              color="var(--theme-primary)"
              delay={350}
            />
          </div>
        )}

        {/* Charts Grid */}
        <div className="mt-6 space-y-4 md:space-y-6">
          {/* Net Income Trend */}
          <div className="apple-card fade-in-up" style={{ animationDelay: '400ms', backgroundColor: 'var(--theme-surface)' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>Cash Flow</h2>
                <p className="text-xs mt-0.5" style={{ color: 'var(--theme-text-secondary)' }}>Monthly net income trend</p>
              </div>
            </div>
            <div className="h-[200px] md:h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart 
                  data={netIncomeData} 
                  margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="positiveGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--theme-income)" stopOpacity={0.3}/>
                      <stop offset="100%" stopColor="var(--theme-income)" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="negativeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--theme-expense)" stopOpacity={0.3}/>
                      <stop offset="100%" stopColor="var(--theme-expense)" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: '#9CA3AF', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    tickFormatter={formatCurrencyShort} 
                    tick={{ fill: '#9CA3AF', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={50}
                  />
                  <Tooltip
                    content={<AppleTooltip formatter={formatCurrency} />}
                    cursor={{ stroke: '#E5E7EB', strokeWidth: 1, strokeDasharray: '4 4' }}
                    wrapperStyle={{ zIndex: 9999 }}
                  />
                  <ReferenceLine y={0} stroke="#E5E7EB" strokeWidth={1} />
                  <Area
                    type="monotone"
                    dataKey="netIncome"
                    name="Net Income"
                    stroke="var(--theme-primary)"
                    strokeWidth={2.5}
                    fill="url(#positiveGradient)"
                    dot={false}
                    activeDot={{ 
                      r: 6, 
                      fill: 'var(--theme-primary)', 
                      stroke: '#fff', 
                      strokeWidth: 2 
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Expenses and Income side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Expenses Chart */}
            <div className="apple-card fade-in-up" style={{ animationDelay: '450ms', backgroundColor: 'var(--theme-surface)' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>Expenses</h2>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--theme-text-secondary)' }}>Spending by category</p>
                </div>
              </div>
              <div className="h-[200px] md:h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={expenseChartData} 
                    margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                  >
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: '#9CA3AF', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      tickFormatter={formatCurrencyShort} 
                      tick={{ fill: '#9CA3AF', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={50}
                    />
                    <Tooltip
                      content={<AppleTooltip formatter={formatCurrency} />}
                      cursor={{ fill: 'rgba(0,0,0,0.02)', radius: 8 }}
                      wrapperStyle={{ zIndex: 9999 }}
                    />
                    {expenseCategories.map((category, index) => (
                      <Bar
                        key={category}
                        dataKey={category}
                        stackId="expenses"
                        fill={getCategoryColor(category, index)}
                        onClick={(data) => handleChartClick(data, 'expense', category)}
                        cursor="pointer"
                        radius={index === expenseCategories.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Category Legend */}
              <div 
                className="flex flex-wrap gap-2 mt-4 pt-4 border-t"
                style={{ borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)' }}
              >
                {Object.entries(expenseCategoryTotals)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .map(([category, total], index) => (
                    <CategoryPill
                      key={category}
                      category={category}
                      color={getCategoryColor(category, index)}
                      amount={total}
                      onClick={() => navigate(`/transactions?category=${encodeURIComponent(category)}&type=expense`)}
                      isPrivacyMode={isPrivacyMode}
                    />
                  ))}
              </div>
            </div>

            {/* Income Chart */}
            <div className="apple-card fade-in-up" style={{ animationDelay: '500ms', backgroundColor: 'var(--theme-surface)' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>Income</h2>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--theme-text-secondary)' }}>Earnings by source</p>
                </div>
              </div>
              <div className="h-[200px] md:h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={incomeChartData} 
                    margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                  >
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: '#9CA3AF', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      tickFormatter={formatCurrencyShort} 
                      tick={{ fill: '#9CA3AF', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={50}
                    />
                    <Tooltip
                      content={<AppleTooltip formatter={formatCurrency} />}
                      cursor={{ fill: 'rgba(0,0,0,0.02)', radius: 8 }}
                      wrapperStyle={{ zIndex: 9999 }}
                    />
                    {incomeCategories.map((category, index) => (
                      <Bar
                        key={category}
                        dataKey={category}
                        stackId="income"
                        fill={getCategoryColor(category, index)}
                        onClick={(data) => handleChartClick(data, 'income', category)}
                        cursor="pointer"
                        radius={index === incomeCategories.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Category Legend */}
              <div 
                className="flex flex-wrap gap-2 mt-4 pt-4 border-t"
                style={{ borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)' }}
              >
                {Object.entries(incomeCategoryTotals)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .map(([category, total], index) => (
                    <CategoryPill
                      key={category}
                      category={category}
                      color={getCategoryColor(category, index)}
                      amount={total}
                      onClick={() => navigate(`/transactions?category=${encodeURIComponent(category)}&type=income`)}
                      isPrivacyMode={isPrivacyMode}
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Spend Analysis */}
        <div className="mt-6 fade-in-up" style={{ animationDelay: '550ms' }}>
          <div className="apple-card" style={{ backgroundColor: 'var(--theme-surface)' }}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>Weekly Spend Analysis</h2>
                <p className="text-sm mt-0.5" style={{ color: 'var(--theme-text-secondary)' }}>Track spending patterns across categories</p>
              </div>
            </div>

            <div>
            {weeklySummaryError && (
              <div
                className="flex items-center gap-3 p-4 rounded-xl mb-4"
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--theme-expense) 10%, var(--theme-surface))',
                  border: '1px solid color-mix(in srgb, var(--theme-expense) 20%, transparent)'
                }}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--theme-expense) 15%, transparent)' }}
                >
                  <ArrowDownRight className="h-4 w-4" style={{ color: 'var(--theme-expense)' }} />
                </div>
                <div>
                  <p className="font-medium text-sm" style={{ color: 'var(--theme-expense)' }}>Unable to load weekly analysis</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--theme-text-secondary)' }}>{weeklySummaryError}</p>
                </div>
              </div>
            )}

            {weeklySummaryLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-4">
                  <div
                    className="w-12 h-12 border-3 rounded-full animate-spin"
                    style={{
                      borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                      borderTopColor: 'var(--theme-primary)'
                    }}
                  />
                  <p className="text-sm font-medium" style={{ color: 'var(--theme-text-secondary)' }}>Analyzing spending patterns...</p>
                </div>
              </div>
            ) : !weeklySummary || weeklySummary.categories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--theme-primary) 10%, transparent)' }}
                >
                  <Calendar className="h-8 w-8" style={{ color: 'var(--theme-primary)' }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>No spending data</h3>
                <p className="text-sm max-w-sm" style={{ color: 'var(--theme-text-secondary)' }}>
                  Add some expenses to see your weekly spending analysis and trends.
                </p>
              </div>
            ) : (
              <>
                {/* Interactive Table */}
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead>
                      <tr style={{ backgroundColor: 'color-mix(in srgb, var(--theme-surface) 95%, transparent)' }}>
                        <th className="text-left px-6 py-4 font-semibold text-sm sticky left-0 z-10" style={{ color: 'var(--theme-text-secondary)', backgroundColor: 'color-mix(in srgb, var(--theme-surface) 95%, transparent)' }}>Category</th>
                          {weeklySummary.weeks.map((week) => (
                            <th key={week.start} className="text-right px-4 py-4 font-semibold text-sm whitespace-nowrap" style={{ color: 'var(--theme-text-secondary)' }}>
                              {week.label}
                            </th>
                          ))}
                          <th className="text-right px-6 py-4 font-semibold text-sm whitespace-nowrap" style={{ color: 'var(--theme-text-secondary)', backgroundColor: 'color-mix(in srgb, var(--theme-surface) 95%, transparent)' }}>
                            <div className="flex items-center justify-end gap-1">
                              <span>L4W Average</span>
                              <Calendar className="h-3 w-3" />
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklySummary.categories.map((row, idx) => {
                          return (
                            <tr
                              key={row.category}
                              className="group hover:bg-opacity-50 transition-colors cursor-pointer"
                              style={{
                                borderTop: idx === 0 ? 'none' : '1px solid color-mix(in srgb, var(--theme-text-secondary) 8%, transparent)',
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--theme-primary) 5%, transparent)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <td
                                className="px-6 py-4 sticky left-0 z-10 cursor-pointer hover:bg-opacity-50 transition-colors"
                                style={{ backgroundColor: 'var(--theme-background)' }}
                                onClick={() => handleTableCellClick(row.category)}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{
                                      backgroundColor: getCategoryColor(row.category, idx)
                                    }}
                                  />
                                  <span className="font-medium text-sm" style={{ color: 'var(--theme-text)' }}>
                                    {row.category}
                                  </span>
                                </div>
                              </td>

                              {row.weekly_totals.map((val, i) => (
                                <td
                                  key={`${row.category}-w-${i}`}
                                  className="px-4 py-4 text-right cursor-pointer hover:bg-opacity-50 transition-colors"
                                  onClick={() => handleTableCellClick(row.category, i)}
                                >
                                  <span className="font-medium text-sm" style={{ color: 'var(--theme-text)' }}>
                                    {formatCurrencyZero(val)}
                                  </span>
                                </td>
                              ))}


                              <td
                                className="px-6 py-4 text-right cursor-pointer hover:bg-opacity-50 transition-colors"
                                style={{ backgroundColor: 'var(--theme-background)' }}
                                onClick={() => handleTableCellClick(row.category)}
                              >
                                <div className="flex items-center justify-end gap-2">
                                  <span className="font-medium text-sm" style={{ color: 'var(--theme-text)' }}>
                                    {formatCurrencyZero(row.l4w_weekly_average)}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="px-6 py-4 border-t bg-gray-50/50" style={{ borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' }}>
                    <div className="flex items-center justify-between text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
                      <span>Hover over rows for details • Percentages show change vs baseline</span>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span>Increasing</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <span>Decreasing</span>
                        </div>
                      </div>
                    </div>
                  </div>
              </>
            )}
          </div>
          </div>
        </div>
      </div>
      
    </div>
  );
};
